import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import TopHeader from '../../template/TopHeader';
import MarketingTopNav from '../MarketingTopNav';
import CampaignsHub from './CampaignsHub';
import { CampaignComposer } from '../../marketing-v2/features/email-composer';
import { SMSBlastComposer } from '../../marketing-v2/features/sms-composer';
import { useMarketingControl } from '../SalonRunsMarketing';
import { CAMPAIGNS_VISIBLE, DISCOUNTS_VISIBLE } from '../../../config/featureFlags';
import { keepFrom } from '../goUp';
import { hydrate as hydrateCodes, findById as findCode } from '../../discounts/discountStore';

/**
 * Marketing › Campaigns
 * Lives alongside the overhauled automations under the /marketing section,
 * reusing the shared catalogue shell + MarketingTopNav (activeTab="campaigns").
 */
function MarketingCampaigns(props) {
  const [view, setView] = useState('list'); // 'list' | 'createEmail' | 'editEmail' | 'createSMS'
  const [startTemplateId, setStartTemplateId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Salon-controlled marketing: the salon runs this stylist's marketing, so
  // there is nothing for them to add here. The server refuses it anyway.
  const control = useMarketingControl();
  const managed = control.managed;

  const backToList = useCallback(() => {
    setView('list');
    setStartTemplateId(null);
    setEditTarget(null);
    setReloadKey((k) => k + 1); // refetch the hub so a just-sent campaign appears
  }, []);

  // Arriving from the marketing calendar or home with "new email" / "new
  // text": open that composer straight away
  const compose = props.location?.state?.compose;
  const composeTemplate = props.location?.state?.template;
  // From a discount code's Share: an email or text made for that code
  const composeCodeId = props.location?.state?.codeId;
  const [offerCode, setOfferCode] = useState(null);
  useEffect(() => {
    if (!compose || managed) return;
    const go = (code) => {
      setOfferCode(code || null);
      if (compose === 'email') { setStartTemplateId(typeof composeTemplate === 'string' ? composeTemplate : null); setView('createEmail'); }
      if (compose === 'sms') setView('createSMS');
      props.history.replace('/marketing-campaigns', keepFrom(props.location));
    };
    if (DISCOUNTS_VISIBLE && composeCodeId) hydrateCodes().then(() => go(findCode(composeCodeId))).catch(() => go(null));
    else go(null);
  }, [compose, managed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Never leave a composer open for a stylist whose marketing the salon runs.
  useEffect(() => {
    if (managed && view !== 'list') backToList();
  }, [managed, view, backToList]);
  if (managed && view !== 'list') return null;

  // The email composer brings its own full-screen CreatorFrame (a fixed overlay),
  // so it renders directly — no extra wrapper (that caused nested scroll/centering).
  if (view === 'createEmail') {
    return <CampaignComposer onBack={backToList} onSave={backToList} initialTemplateId={startTemplateId} initialOfferCode={offerCode} />;
  }
  // Edit an existing draft/scheduled email campaign from the hub
  if (view === 'editEmail') {
    return <CampaignComposer onBack={backToList} onSave={backToList} editCampaign={editTarget} />;
  }
  // The SMS composer now brings its own CreatorFrame, same as email
  if (view === 'createSMS') {
    return <SMSBlastComposer onBack={backToList} onSave={backToList} initialOfferCode={offerCode} />;
  }
  // Continue editing an existing draft/scheduled SMS blast
  if (view === 'editSMS') {
    return <SMSBlastComposer onBack={backToList} onSave={backToList} editCampaign={editTarget} />;
  }

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">
        <TopHeader title="" {...props} />
        <div className="catalogue-page-wrapper">
          <MarketingTopNav activeTab="campaigns">
            {managed ? null : (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="catalogue-add-btn"
                onClick={() => setAddMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={addMenuOpen}
              >
                Add
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              {addMenuOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setAddMenuOpen(false)} />
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 9999,
                    background: '#fff', border: '1px solid #ECECF0', borderRadius: 12,
                    boxShadow: '0 12px 32px rgba(26,26,30,0.14)', padding: 6, minWidth: 200
                  }}>
                    {[
                      ...(CAMPAIGNS_VISIBLE ? [{ label: 'New campaign', action: () => props.history.push('/marketing-campaigns/new') }] : []),
                      { label: CAMPAIGNS_VISIBLE ? 'New email' : 'New email campaign', action: () => { setStartTemplateId(null); setView('createEmail'); } },
                      { label: CAMPAIGNS_VISIBLE ? 'New text' : 'New SMS blast', action: () => setView('createSMS') },
                    ].map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => { setAddMenuOpen(false); item.action(); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                          border: 'none', background: 'transparent', borderRadius: 8,
                          fontSize: 13.5, fontWeight: 600, color: '#1A1A1E', cursor: 'pointer', fontFamily: 'inherit'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#F2F2F7'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            )}
          </MarketingTopNav>
          <div className="catalogue-content-container">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <CampaignsHub
                    key={reloadKey}
                    onCreateEmail={(templateId) => {
                      setStartTemplateId(typeof templateId === 'string' ? templateId : null);
                      setView('createEmail');
                    }}
                    onCreateSMS={() => setView('createSMS')}
                    onOpenCampaign={(c) => { setEditTarget(c); setView('editEmail'); }}
                    onOpenSMS={(c) => { setEditTarget(c); setView('editSMS'); }}
                    openCampaignId={props.location?.state?.openCampaignId}
                    openGroupId={props.location?.state?.openGroupId}
                    newCampaign={props.location?.state?.newCampaign}
                    showCampaigns={props.location?.state?.showCampaigns}
                    onOpenConsumed={() => props.history.replace('/marketing-campaigns', keepFrom(props.location))}
                    managedBySalon={managed}
                    managedBySalonName={control.salonName}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return { ...state };
}

export default connect(mapStateToProps)(MarketingCampaigns);
